import type { Realm } from '@concord/domain';
import { AuthShell, Button, Field, Notice } from '@concord/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ApiError, api, errorMessage } from '../../lib/api';
import { afterSignIn, ME_KEY, safeNext } from '../../lib/session';
import s from './auth.module.css';
import { AUTH_IMAGES } from './auth-image';
import { WhoSwitch } from './WhoSwitch';

const COPY: Record<Realm, { realm: string; home: string }> = {
  patient: { realm: 'Patient portal', home: '/home' },
  staff: { realm: 'Care navigator console', home: '/console' },
};

/**
 * Passwordless sign-in: email, then the 6-digit code from the email. The same email also
 * carries a one-click link, handled by AuthLink. Staff whose email is verified and whose
 * authenticator app is set up can instead sign in with their email and the app's code; staff
 * who use the emailed code are then asked for the app's code (SecondStep).
 */
export function SignIn({ realm }: { realm: Realm }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Staff only: email and the authenticator app's code, no emailed code (verified email, app set up).
  const [useApp, setUseApp] = useState(false);
  const [appCode, setAppCode] = useState('');

  async function signInWithApp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/auth/verify-authenticator', { body: { email, code: appCode } });
      await qc.invalidateQueries({ queryKey: ME_KEY });
      navigate(safeNext(params.get('next'), COPY[realm].home), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setAppCode('');
    } finally {
      setBusy(false);
    }
  }

  async function requestCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ challengeId: string }>('/auth/sign-in', { body: { email, realm } });
      setChallengeId(r.challengeId);
      setCode('');
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid'
          ? 'Enter a valid email address.'
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    if (!challengeId) return;
    setBusy(true);
    setError(null);
    try {
      await api('/auth/verify-code', { body: { challengeId, code } });
      navigate(await afterSignIn(qc, realm, safeNext(params.get('next'), COPY[realm].home)), {
        replace: true,
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const switchTo = (app: boolean) => {
    setUseApp(app);
    setError(null);
    setAppCode('');
  };

  return (
    <AuthShell image={AUTH_IMAGES[realm]}>
      {challengeId === null && useApp ? (
        <form className={s.card} onSubmit={signInWithApp} noValidate>
          <span className={s.realm}>{COPY[realm].realm}</span>
          <h1>Sign in</h1>
          <p className={s.lead}>
            Enter your email and the 6-digit code your authenticator app shows now. No email is sent.
          </p>
          <Field label="Email address">
            {(id) => (
              <input
                id={id}
                className="input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Code from your authenticator app">
            {(id) => (
              <input
                id={id}
                className="input code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={appCode}
                onChange={(e) => setAppCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            )}
          </Field>
          {error && <Notice tone="bad">{error}</Notice>}
          <Button type="submit" size="lg" busy={busy} disabled={!email.trim() || appCode.length !== 6}>
            Sign in
          </Button>
          <button type="button" className={s.switchLink} onClick={() => switchTo(false)}>
            Email me a code instead
          </button>
        </form>
      ) : challengeId === null ? (
        <form className={s.card} onSubmit={requestCode} noValidate>
          <span className={s.realm}>{COPY[realm].realm}</span>
          <h1>Sign in</h1>
          {params.get('reason') === 'idle' && (
            <Notice>
              You were signed out after 15 minutes without activity, to protect patient information.
            </Notice>
          )}
          <p className={s.lead}>We will email you a 6-digit code. There is no password.</p>
          <Field label="Email address" error={error}>
            {(id, describedBy) => (
              <input
                id={id}
                className="input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={error ? true : undefined}
              />
            )}
          </Field>
          <Button type="submit" size="lg" busy={busy} disabled={!email.trim()}>
            Email me a code
          </Button>
          {realm === 'staff' && (
            <button type="button" className={s.switchLink} onClick={() => switchTo(true)}>
              Use my authenticator app
            </button>
          )}
          <p className={s.fine}>
            {realm === 'patient'
              ? 'Your care navigator set up your account. If you do not have one yet, contact them.'
              : 'Staff accounts are created by invitation from the directory.'}
          </p>
        </form>
      ) : (
        <form className={s.card} onSubmit={verify} noValidate>
          <span className={s.realm}>{COPY[realm].realm}</span>
          <h1>Check your email</h1>
          <p className={s.sent}>
            If <b>{email.trim().toLowerCase()}</b> has an account, a code is on its way. It works once, for 10
            minutes. You can also use the Sign in button in the same email.
          </p>
          <Field label="6-digit code" error={error}>
            {(id, describedBy) => (
              <input
                id={id}
                className="input code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                aria-describedby={describedBy}
                aria-invalid={error ? true : undefined}
              />
            )}
          </Field>
          <Button type="submit" size="lg" busy={busy} disabled={code.length !== 6}>
            Sign in
          </Button>
          <div className={s.row}>
            <Button
              variant="ghost"
              onClick={() => {
                setChallengeId(null);
                setError(null);
              }}
            >
              Use a different email
            </Button>
          </div>
          {error?.startsWith('Too many') && (
            <Notice>Wait a few minutes before asking for another code.</Notice>
          )}
        </form>
      )}
      {challengeId === null && <WhoSwitch current={realm} />}
    </AuthShell>
  );
}
